import { validateImageDimensions } from './enterpriseImage'

export async function decodeImageBlob(blob: Blob, signal?: AbortSignal): Promise<HTMLImageElement> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  const url = URL.createObjectURL(blob)
  const img = new Image()
  try {
    await new Promise<void>((resolve, reject) => {
      const finish = (error?: Error) => {
        clearTimeout(timer); signal?.removeEventListener('abort', abort)
        img.onload = img.onerror = null
        error ? reject(error) : resolve()
      }
      const abort = () => { img.src = ''; finish(new DOMException('Aborted', 'AbortError')) }
      const timer = setTimeout(() => { img.src = ''; finish(new Error('bizImage_err_decode')) }, 30000)
      img.onload = () => finish()
      img.onerror = () => finish(new Error('bizImage_err_decode'))
      signal?.addEventListener('abort', abort, { once: true })
      img.src = url
    })
    validateImageDimensions(img.naturalWidth, img.naturalHeight)
    return img
  } finally { URL.revokeObjectURL(url) }
}

/** 签名要求的 Content-Type 必须和字节一致；JPEG 没有透明通道，明确铺白底。 */
export async function encodeImageBlob(blob: Blob, mime: string, signal?: AbortSignal): Promise<Blob> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(mime)) throw new Error('bizImage_err_format')
  const img = await decodeImageBlob(blob, signal)
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth; canvas.height = img.naturalHeight
  try {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('bizImage_err_export')
    if (mime === 'image/jpeg') { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height) }
    ctx.drawImage(img, 0, 0)
    const encoded = await new Promise<Blob>((resolve, reject) => canvas.toBlob(
      value => value && value.type === mime ? resolve(value) : reject(new Error('bizImage_err_export')), mime, 1,
    ))
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
    return encoded
  } finally { canvas.width = canvas.height = 0 }
}

export async function imageToPdf(blob: Blob, signal?: AbortSignal): Promise<Blob> {
  const img = await decodeImageBlob(blob, signal)
  const png = blob.type === 'image/png' ? blob : await encodeImageBlob(blob, 'image/png', signal)
  const { PDFDocument } = await import('pdf-lib')
  const pdf = await PDFDocument.create()
  const embedded = await pdf.embedPng(await png.arrayBuffer())
  const page = pdf.addPage([img.naturalWidth, img.naturalHeight])
  page.drawImage(embedded, { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight })
  const bytes = await pdf.save()
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  return new Blob([new Uint8Array(bytes)], { type: 'application/pdf' })
}

export function saveImageDownload(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url; anchor.download = name.replace(/[\\/\x00-\x1f]/g, '_')
  document.body.appendChild(anchor); anchor.click(); anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30000)
}
